import { RoutePath } from "@/routes";
import { Star, MoreVertical, Trash2, Flag } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { SortableTableHead } from "@/components/shared/SortableTableHead";
import { ProductTableCell } from "@/components/shared/ProductTableCell";
import { LifecycleStateBadge } from "@/components/shared/LifecycleStateBadge";
import type { SortDirection } from "@/components/shared/SortableTableHead";
import type { ProductListItem } from "@/types";
import type { ProductSortField } from "./productsConstants";
import {
  gradeColor,
  ACTIVITY_LABELS,
  AB_TESTS_TAB_PATH,
  MASTER_LIST_TAB_PATH,
} from "./productsConstants";
import { formatCompactRelativeTime } from "@/utils/time.util";
import type { DeleteHeroAsinTarget } from "@/components/shared/DeleteHeroAsinDialog";

interface ProductsTableViewProps {
  products: ProductListItem[];
  readOnly: boolean;
  sortField: ProductSortField | null;
  sortDir: SortDirection;
  onSort: (field: ProductSortField) => void;
  /** AUT-363: `subPath` targets a tab under the product; omitted, the row opens its default. */
  onNavigate: (productId: number, subPath?: string) => void;
  onDeleteRequest: (target: DeleteHeroAsinTarget) => void;
  /** Opens the shared set-lifecycle-state dialog for one product (`intent.md § FR-8`). */
  onSetLifecycleRequest: (product: ProductListItem) => void;
}

export function ProductsTableView({
  products,
  readOnly,
  sortField,
  sortDir,
  onSort,
  onNavigate,
  onDeleteRequest,
  onSetLifecycleRequest,
}: ProductsTableViewProps) {
  return (
    <div className="border-y border-border overflow-x-auto">
      <Table className="min-w-[1040px] table-fixed">
        <colgroup>
          <col className="w-[288px]" />
          {/* Lifecycle — second, immediately after Product. The design puts it third, between
              `Variants` and `Category`, and Signal's table has neither column
              (`components/product.md § Where the design's structure maps onto Signal's`, point 2).
              This `<col>`, the header below and the empty row's `colSpan` move together. */}
          <col className="w-[124px]" />
          <col className="w-[100px]" />
          <col className="w-[120px]" />
          <col className="w-[130px]" />
          <col className="w-[150px]" />
          <col className="w-[170px]" />
          <col className="w-[180px]" />
          {!readOnly && <col className="w-[40px]" />}
        </colgroup>
        <TableHeader>
          <TableRow>
            <SortableTableHead
              field="name"
              activeField={sortField}
              direction={sortDir}
              onSort={onSort}
              className="text-xs"
              tooltip="Hero ASIN name and identifier"
              sticky
            >
              Product
            </SortableTableHead>
            <SortableTableHead
              field="lifecycle"
              activeField={sortField}
              direction={sortDir}
              onSort={onSort}
              className="text-xs"
              tooltip="Manual lifecycle designation — Priority (actively focused) or Core (important, not current focus). Unassigned products show no badge."
            >
              Lifecycle
            </SortableTableHead>
            <SortableTableHead
              field="price"
              activeField={sortField}
              direction={sortDir}
              onSort={onSort}
              className="text-xs text-right"
              tooltip="Current buy-box price"
            >
              Price
            </SortableTableHead>
            <SortableTableHead
              field="rating"
              activeField={sortField}
              direction={sortDir}
              onSort={onSort}
              className="text-xs"
              tooltip="Average customer star rating and total review count"
            >
              Rating
            </SortableTableHead>
            <SortableTableHead
              field="visibility"
              activeField={sortField}
              direction={sortDir}
              onSort={onSort}
              className="text-xs"
              tooltip="Composite visibility grade (0–100) blending rank, share of voice, and coverage across tracked keywords"
            >
              Amazon AI Score
            </SortableTableHead>
            <SortableTableHead
              field="abTests"
              activeField={sortField}
              direction={sortDir}
              onSort={onSort}
              className="text-xs"
              tooltip="Number of A/B tests currently running for this ASIN"
            >
              A/B Tests
            </SortableTableHead>
            <SortableTableHead
              field="themes"
              activeField={sortField}
              direction={sortDir}
              onSort={onSort}
              className="text-xs"
              tooltip="Thematic keyword groupings this product is mapped to"
            >
              Thematic Groupings
            </SortableTableHead>
            <SortableTableHead
              field="recentActivity"
              activeField={sortField}
              direction={sortDir}
              onSort={onSort}
              className="text-xs"
              tooltip="Most recent Signal activity — tests, groupings, ranking changes"
            >
              Recent Activity
            </SortableTableHead>
            {!readOnly && <TableHead className="text-xs w-10" />}
          </TableRow>
        </TableHeader>
        <TableBody>
          {products.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={readOnly ? 8 : 9}
                className="h-24 text-center text-muted-foreground text-sm"
              >
                No products found.
              </TableCell>
            </TableRow>
          ) : (
          products.map((product) => (
            <TableRow
              key={product.id}
              className="cursor-pointer hover:!bg-transparent [&>td]:transition-colors [&:hover>td]:!bg-surface-tint-soft"
              onClick={() => onNavigate(product.id)}
            >
              <TableCell sticky className="min-w-[288px]">
                <ProductTableCell
                  name={product.name}
                  nameHref={`${RoutePath.Products}/${product.id}`}
                  asin={product.asin}
                  imageUrl={product.image_url ?? undefined}
                  size="lg"
                  wrapName
                  asinLinkToAmazon
                />
              </TableCell>
              {/* The badge takes the raw field and normalises it itself — no `?? "unassigned"`
                  here (`components/product.md § The Products page — four surfaces, one write
                  path`). It renders `null` for `unassigned`, so an unmarked row is an empty cell.
                  FR-9: a `viewer` gets no `onChangeRequest`, so the hover key loses its footer
                  while the badge itself still renders. */}
              <TableCell className="text-xs">
                <LifecycleStateBadge
                  state={product.lifecycle_state}
                  onChangeRequest={
                    readOnly ? undefined : () => onSetLifecycleRequest(product)
                  }
                />
              </TableCell>
              <TableCell className="text-xs text-right tabular-nums">
                {product.price != null ? (
                  <span>${Number(product.price).toFixed(2)}</span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="text-xs">
                {product.rating != null ? (
                  <div className="flex flex-col">
                    <span className="inline-flex items-center gap-1">
                      <Star className="h-3 w-3 fill-primary text-primary" />
                      <span className="font-medium">
                        {Number(product.rating).toFixed(1)}
                      </span>
                    </span>
                    <span className="text-muted-foreground">
                      {product.reviews != null
                        ? product.reviews.toLocaleString()
                        : "—"}
                    </span>
                  </div>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="text-xs">
                {product.visibility_score != null && product.visibility_score > 0 ? (
                  <div className="flex flex-col">
                    <span className={`font-semibold ${gradeColor(product.visibility_grade)}`}>
                      {product.visibility_grade ?? "—"}
                    </span>
                    <span className="text-muted-foreground">
                      {product.visibility_score}/100
                    </span>
                  </div>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="text-xs whitespace-nowrap">
                {(product.ab_tests_running ?? 0) > 0 ? (
                  <div className="flex flex-col">
                    <span className="font-medium">
                      {product.ab_tests_running} running
                    </span>
                    <button
                      type="button"
                      className="text-primary hover:underline text-left"
                      onClick={(e) => {
                        e.stopPropagation();
                        onNavigate(product.id, AB_TESTS_TAB_PATH);
                      }}
                    >
                      View all →
                    </button>
                  </div>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="text-xs text-left">
                {(product.themes_count ?? 0) > 0 ? (
                  <div className="flex flex-col">
                    <span className="font-medium">
                      {product.themes_count}{" "}
                      {product.themes_count === 1 ? "theme" : "themes"}
                    </span>
                    <button
                      type="button"
                      className="text-primary hover:underline text-left"
                      onClick={(e) => {
                        e.stopPropagation();
                        onNavigate(product.id, MASTER_LIST_TAB_PATH);
                      }}
                    >
                      View all →
                    </button>
                  </div>
                ) : (
                  <span className="text-muted-foreground">No themes yet</span>
                )}
              </TableCell>
              <TableCell className="text-xs min-w-[180px]">
                {product.recent_activity ? (
                  <div className="flex flex-col">
                    <span className="text-foreground">
                      {ACTIVITY_LABELS[product.recent_activity.type] ?? product.recent_activity.type}
                    </span>
                    <span className="text-muted-foreground">
                      {formatCompactRelativeTime(product.recent_activity.timestamp)}
                    </span>
                  </div>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              {!readOnly && (
                <TableCell
                  className="text-xs w-10"
                  onClick={(e) => e.stopPropagation()}
                >
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-popover z-50">
                      {/* Above the destructive item, and the primary control of the pair: the
                          badge is absent for exactly the products this feature exists to mark
                          (`components/product.md § The Products page — four surfaces, one write
                          path`). */}
                      <DropdownMenuItem
                        onSelect={(e) => {
                          e.preventDefault();
                          onSetLifecycleRequest(product);
                        }}
                      >
                        <Flag className="h-4 w-4 mr-2" />
                        Set lifecycle state
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onSelect={(e) => {
                          e.preventDefault();
                          onDeleteRequest({ id: product.id, name: product.name });
                        }}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Remove from Signal
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              )}
            </TableRow>
          )))}
        </TableBody>
      </Table>
    </div>
  );
}
